import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  Video,
  Book,
  Download,
  ExternalLink,
  FolderOpen,
  Search,
  Link as LinkIcon,
  FileCode,
  File,
  ChevronRight,
  Loader2,
  X,
  Eye
} from 'lucide-react';
import { agentResources } from '../../utils/api';

/**
 * ResourcesSection - Displays agent resources in the agent dashboard
 * @param {number} limit - Optional limit on number of resources to show
 * @param {function} onViewAll - Callback when "View All" is clicked
 * @param {boolean} showHeader - Whether to show the header section (default: true)
 * @param {function} onResourceClick - Callback when a resource is clicked (receives resource object)
 */
const ResourcesSection = ({ limit, onViewAll, showHeader = true, onResourceClick }) => {
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Determine if we're in compact mode (limited resources on dashboard)
  const isCompact = typeof limit === 'number' && limit > 0;

  // Type icons mapping
  const typeIcons = {
    document: FileText,
    video: Video,
    link: LinkIcon,
    script: FileCode,
    other: File
  };

  // Category display colors
  const categoryColors = {
    'Scripts & Dialogues': { bg: 'bg-blue-50', icon: 'bg-blue-100 text-blue-600', border: 'border-blue-100' },
    'Training Videos': { bg: 'bg-purple-50', icon: 'bg-purple-100 text-purple-600', border: 'border-purple-100' },
    'Guides & Checklists': { bg: 'bg-green-50', icon: 'bg-green-100 text-green-600', border: 'border-green-100' },
    'Downloads & Templates': { bg: 'bg-amber-50', icon: 'bg-amber-100 text-amber-600', border: 'border-amber-100' },
    'default': { bg: 'bg-gray-50', icon: 'bg-gray-100 text-gray-600', border: 'border-gray-100' }
  };

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchResources();
  }, [selectedCategory, debouncedSearch]);

  const fetchResources = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }

      const [resourcesRes, categoriesRes] = await Promise.all([
        agentResources.agent.getAll(params),
        agentResources.agent.getCategories()
      ]);

      setResources(resourcesRes.data?.data || []);
      setCategories(categoriesRes.data?.data || []);
    } catch (err) {
      console.error('Error fetching resources:', err);
      setError('Unable to load resources');
    } finally {
      setLoading(false);
    }
  };

  const handleResourceClick = async (resource) => {
    // Record view
    try {
      await agentResources.agent.recordView(resource._id);
    } catch (err) {
      console.error('Error recording view:', err);
    }

    // If onResourceClick prop is provided, use it for navigation
    if (onResourceClick) {
      onResourceClick(resource);
    } else {
      // Default behavior: open resource URL in new tab
      if (resource.url) {
        window.open(resource.url, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const getTypeIcon = (type) => {
    return typeIcons[type] || File;
  };

  const getCategoryColors = (category) => {
    return categoryColors[category] || categoryColors['default'];
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  // Get resources to display (limited or all)
  const displayResources = isCompact ? resources.slice(0, limit) : resources;
  const hasMoreResources = isCompact && resources.length > limit;
  const totalResourceCount = resources.length;

  // Group resources by category for display (only in full view)
  const groupedResources = !isCompact ? displayResources.reduce((acc, resource) => {
    const category = resource.category || 'Uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(resource);
    return acc;
  }, {}) : null;

  const hasResources = resources.length > 0;
  const isSearching = debouncedSearch.length > 0;

  return (
    <div id="resources-section" className="bg-white rounded-xl shadow-sm border border-gray-100">
      {showHeader && (
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mr-3">
                <FolderOpen className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Resources</h3>
                <p className="text-sm text-gray-500">
                  {isCompact ? 'Quick access to coaching materials' : `${totalResourceCount} resources available`}
                </p>
              </div>
            </div>
            {isCompact && hasResources && onViewAll ? (
              <button
                onClick={onViewAll}
                className="flex items-center text-sm text-indigo-600 hover:text-indigo-800 font-medium"
              >
                View All
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            ) : null}
          </div>

          {/* Search bar - show in full view */}
          {!isCompact && (
            <div className="mt-4">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, description, or tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-10 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Category filter tabs - only show in full view when not searching */}
          {!isCompact && !isSearching && categories.length > 1 && (
            <div className="flex flex-wrap gap-2 mt-4">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                  selectedCategory === 'all'
                    ? 'bg-indigo-100 text-indigo-700 font-medium'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                    selectedCategory === cat
                      ? 'bg-indigo-100 text-indigo-700 font-medium'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {/* Search results indicator */}
          {isSearching && !loading && (
            <div className="mt-3 text-sm text-gray-500">
              {hasResources ? (
                <span>Found {totalResourceCount} result{totalResourceCount !== 1 ? 's' : ''} for "{debouncedSearch}"</span>
              ) : (
                <span>No results found for "{debouncedSearch}"</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Search bar when showHeader is false (full tab view) */}
      {!showHeader && (
        <div className="p-6 pb-0">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, description, or tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-10 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Category filter tabs */}
          {!isSearching && categories.length > 1 && (
            <div className="flex flex-wrap gap-2 mt-4">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                  selectedCategory === 'all'
                    ? 'bg-indigo-100 text-indigo-700 font-medium'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                    selectedCategory === cat
                      ? 'bg-indigo-100 text-indigo-700 font-medium'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {/* Search results indicator */}
          {isSearching && !loading && (
            <div className="mt-3 text-sm text-gray-500">
              {hasResources ? (
                <span>Found {totalResourceCount} result{totalResourceCount !== 1 ? 's' : ''} for "{debouncedSearch}"</span>
              ) : null}
            </div>
          )}
        </div>
      )}

      <div className={showHeader ? "p-6" : "p-6"}>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-8 text-red-500">
            <p>{error}</p>
            <button
              onClick={fetchResources}
              className="mt-2 text-sm text-indigo-600 hover:underline"
            >
              Try again
            </button>
          </div>
        ) : hasResources ? (
          <div className="space-y-6">
            {isCompact ? (
              /* Compact view - flat list without categories */
              <div className="space-y-2">
                {displayResources.map((resource) => {
                  const TypeIcon = getTypeIcon(resource.type);
                  const colors = getCategoryColors(resource.category);

                  return (
                    <button
                      key={resource._id}
                      onClick={() => handleResourceClick(resource)}
                      className={`w-full flex items-center p-3 rounded-lg border ${colors.border} ${colors.bg} hover:shadow-md transition-all group text-left`}
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors.icon} flex-shrink-0`}>
                        <TypeIcon className="w-5 h-5" />
                      </div>
                      <div className="ml-3 flex-1 min-w-0">
                        <h5 className="font-medium text-gray-900 truncate group-hover:text-indigo-600 transition-colors">
                          {resource.title}
                        </h5>
                        {resource.description && (
                          <p className="text-sm text-gray-500 truncate">
                            {resource.description}
                          </p>
                        )}
                      </div>
                      <Eye className="w-4 h-4 text-gray-400 group-hover:text-indigo-500 flex-shrink-0 ml-2" />
                    </button>
                  );
                })}
                {hasMoreResources && onViewAll && (
                  <button
                    onClick={onViewAll}
                    className="w-full flex items-center justify-center p-3 text-sm text-indigo-600 hover:text-indigo-800 font-medium hover:bg-indigo-50 rounded-lg transition-colors"
                  >
                    View all {totalResourceCount} resources
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </button>
                )}
              </div>
            ) : isSearching ? (
              /* Search results - flat list */
              <div className="space-y-2">
                {displayResources.map((resource) => {
                  const TypeIcon = getTypeIcon(resource.type);
                  const colors = getCategoryColors(resource.category);

                  return (
                    <button
                      key={resource._id}
                      onClick={() => handleResourceClick(resource)}
                      className={`w-full flex items-center p-4 rounded-lg border ${colors.border} ${colors.bg} hover:shadow-md transition-all group text-left`}
                    >
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colors.icon} flex-shrink-0`}>
                        <TypeIcon className="w-6 h-6" />
                      </div>
                      <div className="ml-4 flex-1 min-w-0">
                        <h5 className="font-medium text-gray-900 group-hover:text-indigo-600 transition-colors">
                          {resource.title}
                        </h5>
                        {resource.description && (
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                            {resource.description}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full">
                            {resource.category || 'General'}
                          </span>
                          {resource.tags?.slice(0, 2).map(tag => (
                            <span key={tag} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                      <Eye className="w-5 h-5 text-gray-400 group-hover:text-indigo-500 flex-shrink-0 ml-4" />
                    </button>
                  );
                })}
              </div>
            ) : (
              /* Full view - grouped by category */
              Object.entries(groupedResources).map(([category, categoryResources]) => (
                <div key={category}>
                  {/* Only show category header if showing all */}
                  {selectedCategory === 'all' && (
                    <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                      {category}
                    </h4>
                  )}
                  <div className="space-y-2">
                    {categoryResources.map((resource) => {
                      const TypeIcon = getTypeIcon(resource.type);
                      const colors = getCategoryColors(category);

                      return (
                        <button
                          key={resource._id}
                          onClick={() => handleResourceClick(resource)}
                          className={`w-full flex items-center p-4 rounded-lg border ${colors.border} ${colors.bg} hover:shadow-md transition-all group text-left`}
                        >
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colors.icon} flex-shrink-0`}>
                            <TypeIcon className="w-6 h-6" />
                          </div>
                          <div className="ml-4 flex-1 min-w-0">
                            <h5 className="font-medium text-gray-900 group-hover:text-indigo-600 transition-colors">
                              {resource.title}
                            </h5>
                            {resource.description && (
                              <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                {resource.description}
                              </p>
                            )}
                            {resource.tags?.length > 0 && (
                              <div className="flex items-center gap-2 mt-2">
                                {resource.tags.slice(0, 3).map(tag => (
                                  <span key={tag} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <Eye className="w-5 h-5 text-gray-400 group-hover:text-indigo-500 flex-shrink-0 ml-4" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        ) : isSearching ? (
          /* No search results */
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h4 className="font-medium text-gray-700 mb-2">No results found</h4>
            <p className="text-sm text-gray-500 mb-4">
              Try searching with different keywords or check your spelling.
            </p>
            <button
              onClick={clearSearch}
              className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
            >
              Clear search
            </button>
          </div>
        ) : (
          /* Empty State */
          <div id="resources-coming-soon" className="bg-gray-50 rounded-lg p-6 text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <FolderOpen className="w-6 h-6 text-gray-400" />
            </div>
            <h4 className="font-medium text-gray-700 mb-1">No Resources Yet</h4>
            <p className="text-sm text-gray-500">
              Your coach will add helpful resources here including scripts, videos, guides, and templates
              to support your growth journey.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResourcesSection;
